import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { ValidatorForm } from 'react-material-ui-form-validator'
import {
  StyledModal,
  AnimatedSuccessTick,
  AnimatedErrorCross
} from 'frame/components'
import StyledModalV2 from 'frame/components/styled-modal-v2'
import Stack from '@mui/material/Stack'

import {
  Box,
  Grid,
  Typography,
  Button,
  Select,
  MenuItem
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { formatDate } from './formatters'
import CarInformationSection from './car-information-section'
import ExceptionInformationSection from './exception-information-section'
import NotesSection from './notes-section'
import {
  sanitizeQueryParams,
  buildFallbackSummary,
  normalizeExceptionValue
} from './form-utils'

import { useSelector, useDispatch } from 'react-redux'
import * as actions from '../../actions'
import {
  hasCompleteExceptionIdentity,
  toExceptionKey
} from '../../common/exception-utils'
import { EXCEPTION_STATUS } from '../../types'

const formContainerSx = {
  backgroundColor: '#ffffffff',
  p: 3,
  borderRadius: 1,
}

const statusAndCarRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 3,
}

const statusLabelSx = {
  fontSize: 14,
  fontWeight: 600,
  mb: 1,
  color: '#1A1A1A',
}

const statusSelectBaseSx = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #98B6BE',
  borderRadius: '8px',
  width: 244,
  height: 40,
  px: '12px',
  py: '8px',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#FFFFFF',
  },
  '&.Mui-focused': {
    backgroundColor: '#FFFFFF',
  },
  '& .MuiSelect-select': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiSelect-select:hover': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiInput-input': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiSelect-icon': {
    marginLeft: '8px',
    right: '12px',
  },
  '& .MuiSelect-iconOpen': {
    marginLeft: '8px',
  },
  '&:hover .MuiSelect-icon': {
    backgroundColor: 'transparent',
  },
}

const statusMenuPropsSx = {
  PaperProps: {
    sx: {
      borderRadius: '8px',
      mt: 1,
      boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
      '& .MuiMenuItem-root': {
        borderBottom: 'none !important',
      },
      '& .MuiList-root': {
        padding: 0,
      },
      '& li': {
        borderBottom: 'none !important',
      },
    },
  },
}

const menuItemBaseSx = {
  fontSize: '14px',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
  },
  '&:hover, &.Mui-selected:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}

const pendingMenuItemSx = {
  ...menuItemBaseSx,
  color: '#1976D2',
}

const reviewedMenuItemSx = {
  ...menuItemBaseSx,
  color: '#1BAA5D',
}

const buttonRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 2,
  mt: 2,
}

const buttonGroupSx = {
  display: 'flex',
  gap: 2,
}

const buttonWidthSx = {
  xs: '100%',
  md: '180px',
}

const reviewButtonWidthSx = {
  xs: '100%',
  md: '220px',
}

const Form = ({
  data,
  flags: flagsProp,
  errorMessage: errorMessageProp,
  onClose,
  onSaveNote,
  onUpdateNote,
  onDeleteNote,
  onAcknowledge,
  onSetPending,
  onNavigateException,
  processingAcknowledge,
  onRegisterCloseHandler,
}) => {
  // Get all notes from the store
  const dispatch = useDispatch()
  // Get notes from a dedicated exceptionNotes state slice
  const notesFromStore = useSelector(
    (state) => state.exceptionNotes?.notes || []
  )
  const fallbackFlags = useSelector(
    (state) => state.reportExceptions?.flags || {}
  )
  const fallbackErrorMessage = useSelector(
    (state) => state.reportExceptions?.apiResponse || null
  )
  const issueState = useSelector((state) => state.exceptionIssue || {})
  const issueDetail = issueState.detail
  const historyState = useSelector((state) => state.exceptionHistory || {})
  const isMultiFillException = useMemo(() => {
    const candidate = issueDetail?.exception ?? data?.exception
    const normalized = normalizeExceptionValue(candidate)
    return normalized === 'multifill' || normalized === 'multifills'
  }, [data?.exception, issueDetail?.exception])

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-NZ', {
        style: 'currency',
        currency: 'NZD',
        minimumFractionDigits: 2,
      }),
    []
  )
  const litresFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-NZ', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }),
    []
  )

  const odometerFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-NZ', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  )

  const formatLitresWithValue = useCallback(
    (litres, value) => {
      const parts = []
      const litresNumber = Number(litres)
      if (Number.isFinite(litresNumber)) {
        parts.push(`${litresFormatter.format(litresNumber)}l`)
      }

      const valueNumber = Number(value)
      if (Number.isFinite(valueNumber)) {
        parts.push(currencyFormatter.format(valueNumber))
      }

      return parts.join(' ') || '-'
    },
    [currencyFormatter, litresFormatter]
  )

  const formatDateTime = useCallback((date, time) => {
    const datePart = date ? moment(date).format('DD MMM YYYY') : ''
    let timePart = ''
    if (time) {
      const parsedTime = moment(
        time,
        [moment.ISO_8601, 'HH:mm', 'HH:mm:ss'],
        true
      )
      timePart = parsedTime.isValid() ? parsedTime.format('h:mma') : time
    }
    return [datePart, timePart].filter(Boolean).join(' ') || '-'
  }, [])

  const getOverTankCapacityLabel = useCallback((litres, tank, fallback) => {
    if (typeof fallback === 'boolean') {
      return fallback ? 'Yes' : 'No'
    }

    const litresNumber = Number(litres)
    const tankNumber = Number(tank)
    if (Number.isFinite(litresNumber) && Number.isFinite(tankNumber)) {
      return litresNumber > tankNumber ? 'Yes' : 'No'
    }

    return '-'
  }, [])

  const issueRequestParams = useMemo(
    () =>
      sanitizeQueryParams({
        agreementNumber: data?.agreementNumber,
        transactionDate: data?.transactionDate,
        transactionTime: data?.transactionTime,
        exception: data?.exception,
      }),
    [
      data?.agreementNumber,
      data?.transactionDate,
      data?.transactionTime,
      data?.exception,
      isMultiFillException,
    ]
  )

  const historyRequestParams = useMemo(
    () =>
      sanitizeQueryParams({
        agreementNumber: data?.agreementNumber,
        transactionDate: data?.transactionDate,
        transactionTime: data?.transactionTime,
        cardnumber: data?.cardnumber,
        vehicleRegistration: data?.vehicleRegistration,
        driverName: data?.driverName,
        exception: data?.exception,
      }),
    [
      data?.agreementNumber,
      data?.transactionDate,
      data?.transactionTime,
      data?.cardnumber,
      data?.vehicleRegistration,
      data?.driverName,
      data?.exception,
    ]
  )

  const flags = flagsProp ?? fallbackFlags
  const errorMessage =
    errorMessageProp ?? issueState.error ?? fallbackErrorMessage

  const refetchParams = useMemo(
    () => ({
      agreementNumber: data?.agreementNumber,
      transactionDate: data?.transactionDate,
      transactionTime: data?.transactionTime,
      cardnumber: data?.cardnumber,
      exception: data?.exception,
    }),
    [
      data?.agreementNumber,
      data?.transactionDate,
      data?.transactionTime,
      data?.cardnumber,
      data?.exception,
    ]
  )

  const canTransitionException = useMemo(
    () => hasCompleteExceptionIdentity(data),
    [
      data?.agreementNumber,
      data?.transactionDate,
      data?.transactionTime,
      data?.cardnumber,
      data?.exception,
    ]
  )
  const fetchExceptionNotes = useCallback(() => {
    if (
      refetchParams.agreementNumber &&
      refetchParams.transactionDate &&
      refetchParams.transactionTime &&
      refetchParams.cardnumber &&
      refetchParams.exception
    ) {
      dispatch(
        actions.getExceptionNotes(null, {
          queryParams: refetchParams,
        })
      )
    }
  }, [dispatch, refetchParams])

  const fetchExceptionIssue = useCallback(() => {
    if (issueRequestParams.agreementNumber) {
      dispatch(actions.getExceptionIssueRequest())
      dispatch(
        actions.getExceptionIssue(null, {
          queryParams: issueRequestParams,
        })
      )
    }
  }, [dispatch, issueRequestParams])

  const fetchExceptionHistory = useCallback(() => {
    const hasHistoryScope =
      historyRequestParams.cardnumber ||
      historyRequestParams.driverName ||
      historyRequestParams.vehicleRegistration

    if (!hasHistoryScope) {
      return
    }

    dispatch(
      actions.getExceptionHistory(null, {
        queryParams: historyRequestParams,
      })
    )
  }, [dispatch, historyRequestParams])

  // Fetch note exceptions on mount with all required params
  useEffect(() => {
    fetchExceptionNotes()
  }, [fetchExceptionNotes])

  useEffect(() => {
    fetchExceptionIssue()
  }, [fetchExceptionIssue])

  useEffect(() => {
    fetchExceptionHistory()
  }, [fetchExceptionHistory])

  const formRef = useRef()
  const noteInputRef = useRef()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [lastNoteAction, setLastNoteAction] = useState(null)

  // Status handling - normalize to lowercase for comparison
  const normalizeStatus = (status) => {
    if (!status || typeof status !== 'string') {
      return EXCEPTION_STATUS.PENDING_REVIEW
    }
    const lower = status.trim().toLowerCase()
    if (lower === EXCEPTION_STATUS.REVIEWED) {
      return EXCEPTION_STATUS.REVIEWED
    }
    return EXCEPTION_STATUS.PENDING_REVIEW
  }

  const originalDataStatus = useRef(normalizeStatus(data?.status)).current

  const [currentStatus, setCurrentStatus] = useState(
    normalizeStatus(data?.status)
  )

  const isPending = originalDataStatus === EXCEPTION_STATUS.PENDING_REVIEW

  const handleStatusChange = (event) => {
    setCurrentStatus(event.target.value)
  }

  const [values, setValues] = useState({
    id: data?.id,
    thirdPartyNumber: data?.thirdPartyNumber,
    notes: data?.notes || '',
  })
  const [editingNoteId, setEditingNoteId] = useState(null)
  const notePlaceholder = editingNoteId
    ? 'Update note details'
    : 'Add a note...'

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState(null)

  // State for unsaved changes confirmation
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false)

  const issueStatus = issueState.status ?? flags?.issue ?? 'none'
  const exceptionTitle =
    data?.exception || issueDetail?.exception || 'Exception'
  const issueSummaryText =
    issueStatus === 'processing'
      ? 'Loading exception details...'
      : issueDetail?.summary ||
        (issueStatus === 'error' && errorMessage
          ? `Exception details are unavailable. ${errorMessage}`
          : buildFallbackSummary(data?.exception, data))

  const litresPurchasedValue = formatLitresWithValue(
    issueDetail?.litresPurchased ?? data?.litres,
    data?.customerValue
  )

  const overTankCapacityValue = getOverTankCapacityLabel(
    issueDetail?.litresPurchased ?? data?.litres,
    issueDetail?.tankCapacity ?? data?.tankSize,
    typeof data?.overTankCapacity === 'boolean'
      ? data?.overTankCapacity
      : undefined
  )

  const productPurchasedValue =
    issueDetail?.productPurchased ||
    data?.productPurchased ||
    data?.product ||
    '-'

  const odometerValue = useMemo(() => {
    const rawValue =
      issueDetail?.odometerReading ??
      issueDetail?.odometer ??
      issueDetail?.odometerValue ??
      data?.odometerReading ??
      data?.odometer ??
      data?.odometerValue

    const numericValue = Number(rawValue)
    if (Number.isFinite(numericValue)) {
      return odometerFormatter.format(numericValue)
    }

    return rawValue || '-'
  }, [data, issueDetail, odometerFormatter])

  const multiFillRows = useMemo(() => {
    const transactions = Array.isArray(issueDetail?.transactions)
      ? issueDetail.transactions
      : []

    if (transactions.length === 0) {
      return []
    }

    const targetDateCandidate =
      data?.transactionDate ?? transactions[0]?.transactionDate ?? null
    const targetMoment =
      targetDateCandidate && moment(targetDateCandidate).isValid()
        ? moment(targetDateCandidate)
        : null

    return transactions.filter((transaction) => {
      const exceptionKey = normalizeExceptionValue(transaction?.exception)
      const isMultiFillException =
        exceptionKey === 'multifill' || exceptionKey === 'multifills'

      if (!isMultiFillException) {
        return false
      }

      if (!targetMoment) {
        return true
      }

      if (!transaction?.transactionDate) {
        return false
      }

      const transactionMoment = moment(transaction.transactionDate)
      return transactionMoment.isValid()
        ? transactionMoment.isSame(targetMoment, 'month')
        : false
    })
  }, [data?.transactionDate, issueDetail?.transactions])
  const onSubmitForm = () => {
    const trimmedNote = values.notes?.trim()
    if (!trimmedNote) {
      return
    }

    if (editingNoteId) {
      onUpdateNote && onUpdateNote(editingNoteId, trimmedNote, refetchParams)
    } else {
      // Compose the note payload as required by the API
      const notePayload = {
        agreementNumber: data.agreementNumber,
        transactionDate: data.transactionDate,
        transactionTime: data.transactionTime,
        cardnumber: data.cardnumber,
        exception: data.exception,
        noteText: trimmedNote,
      }

      onSaveNote(notePayload, refetchParams)
    }

    // Clear text and editing state immediately after dispatching save/update
    setValues((prev) => ({ ...prev, notes: '' }))
    setEditingNoteId(null)
  }

  const handleSaveNotes = () => {
    const trimmedNote = values.notes?.trim()
    if (trimmedNote) {
      onSubmitForm()
    }

    const statusChanged = currentStatus !== originalDataStatus
    if (statusChanged && canTransitionException && !processingAcknowledge) {
      if (currentStatus === EXCEPTION_STATUS.REVIEWED && onAcknowledge) {
        onAcknowledge([toExceptionKey(data)])
      } else if (currentStatus === EXCEPTION_STATUS.PENDING_REVIEW && onSetPending) {
        onSetPending([toExceptionKey(data)])
      }
      onClose()
    }
  }

  const handleReviewedClick = () => {
    if (!onAcknowledge || !canTransitionException || processingAcknowledge) {
      return
    }

    onAcknowledge([toExceptionKey(data)])
    onClose()
  }

  const handleClose = () => {
    const hasUnsavedStatusChange = currentStatus !== originalDataStatus
    const hasUnsavedNote = Boolean(values.notes?.trim())

    if (hasUnsavedStatusChange || hasUnsavedNote) {
      setUnsavedChangesDialogOpen(true)
      return
    }

    onClose()
  }

  useEffect(() => {
    if (onRegisterCloseHandler) {
      onRegisterCloseHandler(handleClose)
    }
  })

  const confirmCloseWithoutSaving = () => {
    setUnsavedChangesDialogOpen(false)
    onClose()
  }

  const onNoteChange = (e) => {
    setValues({
      ...values,
      notes: e.target.value,
    })
  }

  useEffect(() => {
    if (flags?.saveNote === 'processed' || flags?.saveNote === 'error') {
      setLastNoteAction('save')
      setShowConfirmation(true)
    }
  }, [flags?.saveNote])

  useEffect(() => {
    if (flags?.updateNote === 'processed' || flags?.updateNote === 'error') {
      setLastNoteAction('update')
      setShowConfirmation(true)
    }
  }, [flags?.updateNote])

  const beginEditingNote = (note) => {
    if (!note || note.isSystemGenerated) {
      return
    }

    setEditingNoteId(note.id)
    setValues((prev) => ({ ...prev, notes: note.noteText }))
  }

  const cancelEditingNote = () => {
    setEditingNoteId(null)
    setValues((prev) => ({ ...prev, notes: '' }))
  }

  const requestDeleteNote = useCallback(
    (note) => {
      if (!note || note.isSystemGenerated) {
        return
      }

      setPendingDeleteNoteId(note.id)
      setDeleteDialogOpen(true)
    },
    [setDeleteDialogOpen, setPendingDeleteNoteId]
  )

  useEffect(() => {
    if (editingNoteId && noteInputRef.current) {
      const input = noteInputRef.current
      const noteValue = typeof input.value === 'string' ? input.value : ''
      input.focus()
      if (typeof input.setSelectionRange === 'function') {
        const cursorPosition = noteValue.length
        input.setSelectionRange(cursorPosition, cursorPosition)
      }
    }
  }, [editingNoteId])

  const noteActionStatus =
    lastNoteAction === 'update' ? flags?.updateNote : flags?.saveNote

  const driverHistory = Array.isArray(historyState.driver)
    ? historyState.driver
    : []
  const vehicleHistory = Array.isArray(historyState.vehicle)
    ? historyState.vehicle
    : []
  const historyStatus = historyState.status || 'none'
  const historyError = historyState.error || null
  const currentExceptionKey = useMemo(
    () => toExceptionKey(data || {}),
    [data?.agreementNumber, data?.transactionDate, data?.transactionTime]
  )

  const confirmationMessage = () => {
    if (!lastNoteAction) {
      return ''
    }

    if (noteActionStatus === 'processed') {
      return lastNoteAction === 'update'
        ? 'Incident note updated successfully'
        : 'Incident notes saved successfully'
    }

    const fallbackMessage = errorMessage ?? 'Unknown error'
    return lastNoteAction === 'update'
      ? `Update incident note failed. Error: ${fallbackMessage}`
      : `Save incident notes failed. Error: ${fallbackMessage}`
  }

  return (
    <ValidatorForm ref={formRef} onSubmit={onSubmitForm}>
      <Box sx={formContainerSx}>
        {/* Status and Car Information - same row */}
        <Box sx={statusAndCarRowSx}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={statusLabelSx}>
              Status
            </Typography>
            <Select
              id="status-select"
              value={currentStatus}
              onChange={handleStatusChange}
              variant="standard"
              disableUnderline
              readOnly={isPending}
              open={isPending ? false : undefined}
              tabIndex={isPending ? -1 : undefined}
              inputProps={isPending ? { tabIndex: -1 } : undefined}
              IconComponent={isPending ? () => null : KeyboardArrowDownIcon}
              sx={{
                ...statusSelectBaseSx,
                color: currentStatus === EXCEPTION_STATUS.REVIEWED ? '#1BAA5D' : '#1976D2',
                ...(isPending && {
                  pointerEvents: 'none',
                  cursor: 'default',
                  caretColor: 'transparent',
                  '& *': {
                    cursor: 'default !important',
                    caretColor: 'transparent !important',
                    userSelect: 'none',
                  },
                }),
              }}
              MenuProps={statusMenuPropsSx}>
              <MenuItem value="pending review" sx={pendingMenuItemSx}>
                Pending review
              </MenuItem>
              <MenuItem value="reviewed" sx={reviewedMenuItemSx}>
                Reviewed
              </MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <CarInformationSection data={data} />
          </Box>
        </Box>

        <Grid container>
          <ExceptionInformationSection
            data={data}
            exceptionTitle={exceptionTitle}
            issueSummaryText={issueSummaryText}
            litresPurchasedValue={litresPurchasedValue}
            overTankCapacityValue={overTankCapacityValue}
            productPurchasedValue={productPurchasedValue}
            odometerValue={odometerValue}
            multiFillRows={multiFillRows}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
            formatLitresWithValue={formatLitresWithValue}
            getOverTankCapacityLabel={getOverTankCapacityLabel}
            odometerFormatter={odometerFormatter}
            isMultiFill={isMultiFillException}/>
        </Grid>

        <NotesSection
          isMultiFill={isMultiFillException}
          notePlaceholder={notePlaceholder}
          values={values}
          onNoteChange={onNoteChange}
          noteInputRef={noteInputRef}
          editingNoteId={editingNoteId}
          cancelEditingNote={cancelEditingNote}
          notes={notesFromStore}
          onEditNote={beginEditingNote}
          onRequestDelete={requestDeleteNote}
          exceptionTitle={exceptionTitle}
          issueSummaryText={issueSummaryText}
          multiFillRows={multiFillRows}
          formatDateTime={formatDateTime}
          formatLitresWithValue={formatLitresWithValue}
          getOverTankCapacityLabel={getOverTankCapacityLabel}
          odometerFormatter={odometerFormatter}
          driverHistory={driverHistory}
          vehicleHistory={vehicleHistory}
          historyStatus={historyStatus}
          historyError={historyError}
          onViewHistoryItem={onNavigateException}
          currentExceptionKey={currentExceptionKey}/>

        {/* Conditional Buttons based on Status */}
        <Box sx={buttonRowSx}>
          {isPending ? (
            /* Pending Review State Buttons */
            <>
              <Box sx={buttonGroupSx}>
                <Box width={buttonWidthSx}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveNotes}
                    fullWidth>
                    Save notes
                  </Button>
                </Box>
                <Box width={buttonWidthSx}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClose}
                    fullWidth>
                    Close
                  </Button>
                </Box>
              </Box>
              <Box width={reviewButtonWidthSx}>
                <Button
                  type="button"
                  variant="contained"
                  color="success"
                  fullWidth
                  disabled={!canTransitionException}
                  onClick={handleReviewedClick}>
                  I have reviewed this
                </Button>
              </Box>
            </>
          ) : (
            /* Reviewed State Buttons */
            <>
              <Box width={buttonWidthSx}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClose}
                  fullWidth>
                  Close
                </Button>
              </Box>
              <Box width={buttonWidthSx}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveNotes}
                  fullWidth>
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <StyledModal
        open={showConfirmation}
        onCancel={() => {
          setShowConfirmation(false)
          setLastNoteAction(null)
        }}
        options={['noButtons']}
        animatedIcon={
          noteActionStatus === 'processed' ? (
            <AnimatedSuccessTick size={50} />
          ) : (
            <AnimatedErrorCross size={50} />
          )
        }>
        {confirmationMessage()}
      </StyledModal>
      {/*  Delete Confirmation Dialog */}
      <StyledModalV2
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        content={
          <Typography variant="h4" textAlign="center">
            Are you sure you want to delete this note?
          </Typography>
        }
        actions={
          <Stack
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="center"
            gap={1}>
            <Box width={buttonWidthSx}>
              <Button
                fullWidth
                color="error"
                variant="outlined"
                onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
            </Box>
            <Box width={buttonWidthSx}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={() => {
                  if (onDeleteNote && pendingDeleteNoteId) {
                    onDeleteNote(pendingDeleteNoteId, refetchParams)
                  }
                  setDeleteDialogOpen(false)
                  setPendingDeleteNoteId(null)
                }}>
                Confirm
              </Button>
            </Box>
          </Stack>
        }/>
      {/* Unsaved Changes Confirmation Dialog */}
      <StyledModalV2
        open={unsavedChangesDialogOpen}
        onClose={() => setUnsavedChangesDialogOpen(false)}
        content={
          <Typography variant="h4" textAlign="center">
            You have unsaved changes. Are you sure you want to close?
          </Typography>
        }
        actions={
          <Stack
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="center"
            gap={1}>
            <Box width={buttonWidthSx}>
              <Button
                fullWidth
                color="error"
                variant="outlined"
                onClick={() => setUnsavedChangesDialogOpen(false)}>
                Cancel
              </Button>
            </Box>
            <Box width={buttonWidthSx}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={confirmCloseWithoutSaving}>
                Confirm
              </Button>
            </Box>
          </Stack>
        }/>
    </ValidatorForm>
  )
}

Form.propTypes = {
  data: PropTypes.object.isRequired,
  flags: PropTypes.object,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaveNote: PropTypes.func.isRequired,
  onUpdateNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
  onAcknowledge: PropTypes.func,
  onSetPending: PropTypes.func,
  processingAcknowledge: PropTypes.bool,
  onNavigateException: PropTypes.func,
  onRegisterCloseHandler: PropTypes.func,
}

Form.defaultProps = {
  onUpdateNote: undefined,
  onDeleteNote: undefined,
  onAcknowledge: undefined,
  onSetPending: undefined,
  processingAcknowledge: false,
  onNavigateException: undefined,
  flags: undefined,
  errorMessage: undefined,
  onRegisterCloseHandler: undefined,
}

export default Form
